// Responsive breakpoints and utilities for mobile-first design

export const RESPONSIVE_BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
} as const;

export const MOBILE_LAYOUT_CONSTANTS = {
  headerHeight: '3.5rem', // 14 in Tailwind (56px)
  tabBarHeight: '4rem', // 16 in Tailwind (64px)
  minTouchTarget: '44px', // Minimum touch target size
  panelTransition: 'transform 300ms ease-in-out',
  swipeThreshold: 50, // Minimum distance for swipe gesture
} as const;

export const RESPONSIVE_PANEL_WIDTHS = {
  // Desktop layout
  desktop: {
    sidebar: '29%',
    main: '71%',
  },
  // Tablet layout
  tablet: {
    sidebar: '320px',
    main: 'calc(100% - 320px)',
  },
  // Mobile layout - full width panels
  mobile: {
    sidebar: '100%',
    main: '100%',
  },
} as const;

export const MOBILE_VIEW_MODES = {
  AGENT: 'agent',
  VISUAL: 'visual',
  CODE: 'code',
  PREVIEW: 'preview',
  HISTORY: 'history',
} as const;

// Utility functions for responsive behavior
export const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < RESPONSIVE_BREAKPOINTS.mobile;
};

export const isTablet = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= RESPONSIVE_BREAKPOINTS.mobile &&
         window.innerWidth < RESPONSIVE_BREAKPOINTS.tablet;
};

export const isDesktop = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= RESPONSIVE_BREAKPOINTS.tablet;
};

// Touch gesture utilities
export interface TouchPosition {
  x: number;
  y: number;
}

export interface SwipeDirection {
  direction: 'left' | 'right' | 'up' | 'down' | null;
  distance: number;
}

export const calculateSwipeDirection = (
  startPos: TouchPosition,
  endPos: TouchPosition
): SwipeDirection => {
  const deltaX = endPos.x - startPos.x;
  const deltaY = endPos.y - startPos.y;
  const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2);

  if (distance < MOBILE_LAYOUT_CONSTANTS.swipeThreshold) {
    return { direction: null, distance: 0 };
  }

  const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

  if (angle >= -45 && angle <= 45) {
    return { direction: 'right', distance };
  } else if (angle >= 135 || angle <= -135) {
    return { direction: 'left', distance };
  } else if (angle > 45 && angle < 135) {
    return { direction: 'down', distance };
  } else {
    return { direction: 'up', distance };
  }
};